import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { providersAPI, locationsAPI } from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const ProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [provinceId, setProvinceId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [sectorId, setSectorId] = useState('');

  useEffect(() => {
    locationsAPI.getAll().then(res => {
      setProvinces(res.data.provinces || []);
      setDistricts(res.data.districts || []);
      setSectors(res.data.sectors || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (provinceId) params.province_id = provinceId;
    if (districtId) params.district_id = districtId;
    if (sectorId) params.sector_id = sectorId;
    providersAPI.search(params)
      .then((res) => setProviders(res.data.providers || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, provinceId, districtId, sectorId]);

  const filteredDistricts = provinceId ? districts.filter(d => String(d.province_id) === String(provinceId)) : [];
  const filteredSectors = districtId ? sectors.filter(s => String(s.district_id) === String(districtId)) : [];

  const selectClass = "border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none text-sm transition-all bg-white";

  return (
    <div className="min-h-screen bg-gray-50/50">
      <SEO title="Service Providers" description="Find verified and trusted service providers in Rwanda. Browse ratings, reviews, portfolios, and hire top-rated professionals in Kigali, Musanze, Huye, Rubavu and all provinces." keywords="service providers Rwanda, professionals Kigali, verified providers, trusted workers Rwanda, hire experts Kigali, contractor Rwanda, freelancer, skilled workers, artisan Rwanda, technician Kigali, rated professionals, reviewed service providers, top rated workers Rwanda, best providers Kigali, experienced professionals Rwanda" />
      <div className="bg-primary-600 relative overflow-hidden text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-80 h-80 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Find Service Providers</h1>
          <p className="text-white/70 text-base">Browse trusted professionals across Rwanda</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input type="text" placeholder="Search providers..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className={selectClass} />
            <select value={provinceId} onChange={(e) => { setProvinceId(e.target.value); setDistrictId(''); setSectorId(''); }} className={selectClass}>
              <option value="">All Provinces</option>
              {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {filteredDistricts.length > 0 && (
              <select value={districtId} onChange={(e) => { setDistrictId(e.target.value); setSectorId(''); }} className={selectClass}>
                <option value="">All Districts</option>
                {filteredDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            {filteredSectors.length > 0 && (
              <select value={sectorId} onChange={(e) => setSectorId(e.target.value)} className={selectClass}>
                <option value="">All Sectors</option>
                {filteredSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
          {(provinceId || search) && (
            <button onClick={() => { setSearch(''); setProvinceId(''); setDistrictId(''); setSectorId(''); }}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
              ✕ Clear filters
            </button>
          )}
        </div>

        {loading ? <LoadingSpinner /> : providers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-4">👤</div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No providers found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((p) => (
              <Link key={p.id} to={`/providers/${p.id}`}
                className="bg-white rounded-2xl shadow-sm hover:shadow-xl hover:shadow-black/5 transition-all duration-300 p-6 border border-gray-100 hover:-translate-y-1 group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-lg shadow-md group-hover:shadow-lg transition-shadow">
                    {p.first_name?.[0]}{p.last_name?.[0]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{p.business_name || `${p.first_name} ${p.last_name}`}</h3>
                    {p.location && <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                      {p.location}
                    </p>}
                  </div>
                </div>
                {p.service_description && (
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4">{p.service_description}</p>
                )}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} className={`w-4 h-4 ${s <= Math.round(p.avg_rating || 0) ? 'text-yellow-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-sm text-gray-600 font-medium ml-1">{Number(p.avg_rating || 0).toFixed(1)}</span>
                  </div>
                  <span className="text-gray-400 text-sm">{p.total_reviews || 0} reviews</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvidersPage;
